import { leaveBreadcrumb } from 'cfa-react-logging';
import authClient from '../authClient';
import ooeConstants from '../constants';
import { FavoriteOrder, FavoriteOrderRequest, Order } from '../order/schemas';
import { PaymentAccountSearchRequest, PaymentOption } from '../payment/schemas';
import { rexCateringClient } from '../shared/clients';
import { handleFetchFail, parseAsJson } from '../util/httpUtils';
import { FindCustomersByEmailAddressResponse, findCustomersByEmailAddressResponseSchema } from './schemas';

export const searchForCustomerProfile = async ({
  firstName,
  lastName,
  locationNumber,
  cfaOneOnly,
}: {
  firstName: string;
  lastName: string;
  locationNumber: string;
  cfaOneOnly?: boolean;
}): Promise<Customer[]> => {
  const baseUrl = ooeConstants.URL.PROFILE;
  const url = `${baseUrl}/customers/locations/1.0/${locationNumber}/search`;

  const method = 'POST';
  const bugsnag = {
    breadcrumb: 'Get Name Search Results',
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_PROFILE,
    context: 'Name Search Results',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    url,
    method,
    context: bugsnag.context,
  });

  const body = JSON.stringify({
    firstName,
    lastName,
    cfaOneOnly: cfaOneOnly ? 'true' : undefined,
  });

  const apiKey = await authClient.getOrRenewAccessToken();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `JWTBearer ${apiKey}`,
  };
  const response = await fetch(url, { body, method, headers }).catch(handleFetchFail);
  return parseAsJson(response);
};

export const findCustomersByEmailAddress = async ({
  emailAddress,
}: {
  emailAddress: string;
}): Promise<FindCustomersByEmailAddressResponse> => {
  const baseUrl = ooeConstants.URL.D2C;
  const url = `${baseUrl}/cfa.d2c.customer.v1.CustomerService/FindCustomersByEmailAddress`;
  const method = 'POST';
  const bugsnag = {
    breadcrumb: 'Find Customers By Email Address',
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_D2C,
    context: 'Find Customers By Email Address',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    url,
    method,
    context: bugsnag.context,
  });

  const body = JSON.stringify({ emailAddress });

  const apiKey = await authClient.getOrRenewAccessToken();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `JWTBearer ${apiKey}`,
  };
  const response = await fetch(url, { body, method, headers }).catch(handleFetchFail);
  return findCustomersByEmailAddressResponseSchema.parse(await parseAsJson(response));
};

export const getCustomerProfilesByPhoneNumber = async ({
  phoneNumber,
}: {
  phoneNumber: string;
}): Promise<{ userProfiles?: PhoneSearchCustomer[] }> => {
  const baseUrl = ooeConstants.URL.PROFILE;
  const url = `${baseUrl}/users/2.0/phone?phone=${phoneNumber}`;
  const method = 'GET';

  leaveBreadcrumb('Getting customer profiles by phone number', {});

  const apiKey = await authClient.getOrRenewAccessToken();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `JWTBearer ${apiKey}`,
  };
  const response = await fetch(url, { method, headers }).catch(handleFetchFail);
  return parseAsJson(response);
};

export const getPaymentAccount = async ({
  cfaId,
  isRegisteredMember,
}: {
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<PaymentOption[]> => {
  const bugsnag = {
    breadcrumb: 'Get Payment Account',
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_PAYMENTS,
    context: 'Get Payment Account',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    context: bugsnag.context,
  });

  return rexCateringClient
    .getPaymentAccount({ cfaId, isRegisteredMember })
    .then((response) => response.paymentOptions ?? []);
};

export const getPaymentAccountById = async ({
  accountId,
  cfaId,
  isRegisteredMember,
  signal,
}: {
  accountId: string;
  cfaId: string;
  isRegisteredMember: boolean;
  signal?: AbortSignal;
}): Promise<PaymentOption> => {
  const bugsnag = {
    breadcrumb: 'Get Payment Account By ID',
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_PAYMENTS,
    context: 'Get Payment Account By ID',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    context: bugsnag.context,
  });

  // Lets a superseded edit-order restore (see listeners.ts) cancel the rex-catering call
  // instead of only ignoring its response once it returns.
  return rexCateringClient.getPaymentAccountById({ accountId, cfaId, isRegisteredMember }, { signal });
};

export const searchForPaymentAccounts = async ({
  zip,
  card,
  cfaId,
  isRegisteredMember,
}: {
  zip: string;
  card: Card;
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<PaymentOption[]> => {
  const bugsnag = {
    breadcrumb: 'Search For Payment Accounts',
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_PAYMENTS,
    context: 'Search For Payment Accounts',
  };

  const cardAlias = card.accountDisplay;
  const cardType = card.cardType;
  leaveBreadcrumb(bugsnag.breadcrumb, {
    context: bugsnag.context,
    cardAlias,
    cardType,
  });

  const payload: PaymentAccountSearchRequest = {
    cardAlias,
    cardType,
    zipCode: zip,
    cfaId,
  };
  return rexCateringClient
    .searchForPaymentAccounts({ ...payload, isRegisteredMember })
    .then((response) => response.paymentOptions ?? []);
};

export const getGuestCateringOrderHistory = async ({
  numToLoad,
  cfaId,
  isRegisteredMember,
}: {
  numToLoad: number;
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<Order[]> => {
  const bugsnag = {
    breadcrumb: 'Get Guest Catering Order History',
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Get Guest Catering Order History',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    context: bugsnag.context,
  });

  return rexCateringClient
    .getGuestCateringOrderHistory({ numToLoad, cfaId, isRegisteredMember })
    .then((response) => response.orders ?? []);
};

export const getFavoriteCateringOrders = async ({
  locationNumber,
  destination,
  cfaId,
  isRegisteredMember,
}: {
  locationNumber: string;
  destination: string;
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<FavoriteOrder[]> => {
  const bugsnag = {
    breadcrumb: 'Get Guest Favorite Catering Orders',
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Get Guest Favorite Catering Orders',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    context: bugsnag.context,
  });

  return rexCateringClient
    .getFavoriteCateringOrders({
      locationNumber,
      destination,
      cfaId,
      isRegisteredMember,
    })
    .then(
      (response) =>
        response.favoriteOrders?.map((order) => ({ ...order, orderDetail: order.orderDetail! })) ?? [],
    );
};

export const addToFavoriteOrders = async ({
  orderId,
  name,
  cfaId,
  isRegisteredMember,
}: {
  orderId: string;
  name: string;
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<void> => {
  const bugsnag = {
    breadcrumb: 'Add To Guest Favorite Catering Orders',
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Add To Guest Favorite Catering Orders',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    context: bugsnag.context,
  });

  const payload: FavoriteOrderRequest = {
    orderId,
    name,
  };
  await rexCateringClient.addToFavoriteOrders({ ...payload, cfaId, isRegisteredMember });
};

export const removeFromFavoriteOrders = async ({
  favoriteOrderId,
  cfaId,
  isRegisteredMember,
}: {
  favoriteOrderId: string;
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<void> => {
  const bugsnag = {
    breadcrumb: 'Remove From Guest Favorite Catering Orders',
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Remove From Guest Favorite Catering Orders',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    context: bugsnag.context,
  });

  await rexCateringClient.removeFromFavoriteOrders({ favoriteOrderId, cfaId, isRegisteredMember });
};

export const updateFavoriteOrderName = async ({
  favoriteOrderId,
  favoriteName,
  cfaId,
  isRegisteredMember,
}: {
  favoriteOrderId: string;
  favoriteName: string;
  cfaId: string;
  isRegisteredMember: boolean;
}): Promise<void> => {
  const bugsnag = {
    breadcrumb: 'Update Guest Favorite Catering Order Name',
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Update Guest Favorite Catering Order Name',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    context: bugsnag.context,
  });

  await rexCateringClient.updateFavoriteOrderName({
    favoriteOrderId,
    favoriteName,
    cfaId,
    isRegisteredMember,
  });
};
